import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { useSettings } from '@/hooks/useSettings';
import styles from './FixedScheduleReminder.module.css';

interface FixedScheduleReminderProps {
  onDismiss?: () => void;
}

type ReminderState = 'normal' | 'warning' | 'urgent';

/**
 * Fixed Schedule Reminder - Cal Newport's fixed-schedule productivity
 *
 * Shows a visual countdown to the fixed end time for the workday.
 * Helps enforce the discipline of finishing work at a consistent time.
 */
export function FixedScheduleReminder({ onDismiss }: FixedScheduleReminderProps) {
  const { settings } = useSettings();
  const [minutesRemaining, setMinutesRemaining] = useState<number>(0);
  const [reminderState, setReminderState] = useState<ReminderState>('normal');
  const [isDismissed, setIsDismissed] = useState<boolean>(false);
  const notificationSentRef = useRef<boolean>(false);

  // Get today's date key for localStorage
  const getTodayKey = useCallback(() => {
    return `fixedScheduleDismissed_${format(new Date(), 'yyyy-MM-dd')}`;
  }, []);

  // Check if already dismissed today
  useEffect(() => {
    const dismissedToday = localStorage.getItem(getTodayKey());
    if (dismissedToday === 'true') {
      setIsDismissed(true);
    }
  }, [getTodayKey]);

  // Parse end time from HH:MM format to minutes from midnight
  const parseTimeToMinutes = useCallback((timeStr: string): number => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  }, []);

  // Calculate time remaining and update state
  const updateTimeRemaining = useCallback(() => {
    if (!settings.fixedScheduleEnabled) return;

    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const endMinutes = parseTimeToMinutes(settings.fixedScheduleEndTime);
    const remaining = endMinutes - currentMinutes;

    setMinutesRemaining(remaining);

    // Determine reminder state
    if (remaining <= 0) {
      setReminderState('urgent');
    } else if (remaining <= settings.fixedScheduleReminderMinutes) {
      setReminderState('warning');
    } else {
      setReminderState('normal');
    }
  }, [
    settings.fixedScheduleEnabled,
    settings.fixedScheduleEndTime,
    settings.fixedScheduleReminderMinutes,
    parseTimeToMinutes,
  ]);

  // Request notification permission on mount
  useEffect(() => {
    if (
      settings.notifications &&
      'Notification' in window &&
      Notification.permission === 'default'
    ) {
      Notification.requestPermission();
    }
  }, [settings.notifications]);

  // Send browser notification when entering warning state
  useEffect(() => {
    if (
      reminderState === 'warning' &&
      !notificationSentRef.current &&
      settings.notifications &&
      'Notification' in window &&
      Notification.permission === 'granted'
    ) {
      new Notification('Fixed Schedule Reminder', {
        body: `${minutesRemaining} minutes until your scheduled end time. Start wrapping up!`,
        icon: '/icons/icon-192x192.png',
        tag: 'fixed-schedule-reminder',
      });
      notificationSentRef.current = true;
    }

    // Reset notification flag when back to normal
    if (reminderState === 'normal') {
      notificationSentRef.current = false;
    }
  }, [reminderState, minutesRemaining, settings.notifications]);

  // Update every minute
  useEffect(() => {
    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 60000);
    return () => clearInterval(interval);
  }, [updateTimeRemaining]);

  // Handle dismiss
  const handleDismiss = useCallback(() => {
    localStorage.setItem(getTodayKey(), 'true');
    setIsDismissed(true);
    onDismiss?.();
  }, [getTodayKey, onDismiss]);

  // Format time remaining for display
  const formatTimeRemaining = useCallback((): string => {
    if (minutesRemaining <= 0) {
      const overMinutes = Math.abs(minutesRemaining);
      if (overMinutes >= 60) {
        const hours = Math.floor(overMinutes / 60);
        const mins = overMinutes % 60;
        return `${hours}h ${mins}m past`;
      }
      return `${overMinutes}m past`;
    }

    if (minutesRemaining >= 60) {
      const hours = Math.floor(minutesRemaining / 60);
      const mins = minutesRemaining % 60;
      if (mins === 0) {
        return `${hours}h`;
      }
      return `${hours}h ${mins}m`;
    }

    return `${minutesRemaining}m`;
  }, [minutesRemaining]);

  // Don't render if disabled or dismissed
  if (!settings.fixedScheduleEnabled || isDismissed) {
    return null;
  }

  // Don't show if more than 2 hours remaining (keep it unobtrusive)
  if (minutesRemaining > 120) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        className={`${styles.reminder} ${styles[reminderState]}`}
        initial={{ opacity: 0, y: -20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        role="alert"
        aria-live="polite"
      >
        <div className={styles.iconWrapper}>
          <span className={styles.icon} aria-hidden="true">
            {reminderState === 'urgent' ? '!' : reminderState === 'warning' ? '\u23F0' : '\u231B'}
          </span>
        </div>

        <div className={styles.content}>
          <p className={styles.title}>
            {reminderState === 'urgent'
              ? 'Workday ended'
              : reminderState === 'warning'
                ? 'Wrapping up'
                : 'Fixed schedule'}
          </p>
          <p className={styles.time}>
            <span className={styles.timeValue}>{formatTimeRemaining()}</span>
            {minutesRemaining > 0 ? (
              <span className={styles.timeLabel}>until {settings.fixedScheduleEndTime}</span>
            ) : (
              <span className={styles.timeLabel}>end time</span>
            )}
          </p>
        </div>

        <motion.button
          className={styles.dismissButton}
          onClick={handleDismiss}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Dismiss reminder for today"
        >
          <span aria-hidden="true">Dismiss</span>
        </motion.button>
      </motion.div>
    </AnimatePresence>
  );
}

export default FixedScheduleReminder;
